import request from './base';

import i18n from 'i18next';
import type { BaseRequestRes, CusRequestParams, Method } from '@bluevision/tools';
import type { RequestOptionsInit } from 'umi-request';

import { Locales } from '@/i18n';

import { encrypt } from '@bluevision/tools';

export const CDN_URL = 'https://bluevs-static.domobcdn.com/vision_line/';

const baseApi = '/api/ka';

const REQUEST_LANGUAGE_FLAG = {
  ZH: 'zh-hans',
  EN: 'en',
};

// 后端的语言环境标识使用了和国际化不一致的协议标准，所以需要完成一次映射
const LANGUAGE_FLAG_MAP = {
  [Locales.zhCN]: REQUEST_LANGUAGE_FLAG.ZH,
  [Locales.enUS]: REQUEST_LANGUAGE_FLAG.EN,
};

function baseRequest<T>(method: Method, ...other: CusRequestParams) {
  const [url, data, notice, id, addition] = other;
  return request<T>(id ? `${url}/${id}` : url, {
    method,
    data: method === 'GET' || method === 'DELETE' ? undefined : data,
    params: method === 'GET' || method === 'DELETE' ? data : undefined,
    notice,
    ...addition,
  } as RequestOptionsInit);
}

const publicKey = `-----BEGIN RSA PUBLIC KEY-----
MIIBCgKCAQEA0I7j9RrPP9tqoagcYv0UI7sWe5WBZqDrpl+Mk8mpBeVReLK9tqf6
zaHZqkp0kcPsmWqs2OAq+A18yZnuwje8xDrAp1ybUm4NdYdZxNzkQ/ovg4sEDWH3
gafzCRfmCyqdq54pqAx8aWcVsj3WtT00eMwhsjORavpl2SgaFVbtgePzt+SxuHzk
QYq0UgKLhQoyO6smh5AYTVuJYix2yz0ms+ENLwaYGBexCDs8N81y7u5xHlBcnFVj
fuhiavi0vWGPNx+t0WaJM4iHv3zdLD8m9rMvwATP/1jPKicA1YlGcJ0088zuiM08
fi9cYB+fZw9JaY+LVJmhojOMR/kBrRjdkQIDAQAB
-----END RSA PUBLIC KEY-----`;

function executeRequest<T>(method: Method, ...params: CusRequestParams): BaseRequestRes<T> {
  const [url, data, notice, id, addition, otherParams] = params;
  const currentUrl = `${otherParams?.baseAPI ?? baseApi}/${url}`;

  const noticeText = notice ?? '';

  // 时间戳+随机数
  const clientFetchId = encrypt(Date.now() + Math.random().toString(16).slice(2), publicKey);

  const currentAddition = {
    ...addition,
    headers: {
      ...addition?.headers,
      'Accept-Language': LANGUAGE_FLAG_MAP[i18n.language] ?? REQUEST_LANGUAGE_FLAG.ZH,
      'Client-Fetch-Id': !!clientFetchId ? clientFetchId : '',
    },
  };

  if (['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS', 'HEAD'].includes(method)) {
    return baseRequest<T>(method, currentUrl, data, noticeText, id, currentAddition, otherParams);
  } else {
    throw new Error(`Unsupported method: ${method}`);
  }
}

export function patch<T>(...params: CusRequestParams): BaseRequestRes<T> {
  return executeRequest<T>('PATCH', ...params);
}
export function post<T>(...params: CusRequestParams): BaseRequestRes<T> {
  return executeRequest<T>('POST', ...params);
}
export function read<T>(...params: CusRequestParams): BaseRequestRes<T> {
  return executeRequest<T>('GET', ...params);
}
export function put<T>(...params: CusRequestParams): BaseRequestRes<T> {
  return executeRequest<T>('PUT', ...params);
}
export function del<T>(...params: CusRequestParams): BaseRequestRes<T> {
  return executeRequest<T>('DELETE', ...params);
}
